import { useState, useEffect } from "react";
import { Button } from "@jobber/components/Button";
import { Content } from "@jobber/components/Content";
import { DataTable } from "@jobber/components/DataTable";
import { Heading } from "@jobber/components/Heading";
import { InputDate } from "@jobber/components/InputDate";
import { StatusLabel } from "@jobber/components/StatusLabel";
import { Text } from "@jobber/components/Text";

import DashboardTab from "../../components/Dashboard/DashboardTab";
import { getSyncHistory, syncJobs } from "../../services";
import { SyncJob, SYNC_JOB_STATUS_LABEL, SYNC_JOB_STATUS_DISPLAY } from "../../types/SyncJob";
import { showError } from "../../helpers";

function displayDate(isoString: string): string {
    return isoString.split("T")[0];
}

function displayTimestamp(isoString: string): string {
    const [date, time] = isoString.split("T");
    const [hh, mm] = time.split(":");
    return `${date} ${hh}:${mm}`;
}

function SyncJobs() {
    const [startDate, setStartDate] = useState<Date | undefined>(undefined);
    const [endDate, setEndDate] = useState<Date | undefined>(undefined);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncHistory, setSyncHistory] = useState<SyncJob[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);

    const loadHistory = async () => {
        setIsLoadingHistory(true);
        try {
            const history = await getSyncHistory();
            setSyncHistory(history);
        } catch (e: any) {
            showError("Error loading sync history: " + e.message);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    useEffect(() => {
        loadHistory();
    }, []);

    const handleSync = async () => {
        if (!startDate || !endDate) return;
        setIsSyncing(true);
        try {
            await syncJobs(startDate, endDate);
            await loadHistory();
            setStartDate(undefined);
            setEndDate(undefined);
        } catch (e: any) {
            showError("Error syncing jobs: " + e.message);
        } finally {
            setIsSyncing(false);
        }
    };

    return (
        <DashboardTab>
            <Content spacing="base">
                <Text>
                    Select a date range to sync jobs for all connected franchises from Jobber to Listen360.
                </Text>
                <div style={{ display: "flex", gap: "16px" }}>
                    <div style={{ width: "160px" }}>
                        <InputDate
                            placeholder="Start Date"
                            value={startDate}
                            maxDate={endDate}
                            onChange={(date) => setStartDate(date)}
                        />
                    </div>
                    <div style={{ width: "160px" }}>
                        <InputDate
                            placeholder="End Date"
                            value={endDate}
                            minDate={startDate}
                            onChange={(date) => setEndDate(date)}
                        />
                    </div>
                </div>
                <Button
                    label={isSyncing ? "Syncing..." : "Sync Jobs"}
                    onClick={handleSync}
                    loading={isSyncing}
                    disabled={!startDate || !endDate || isSyncing}
                />
            </Content>

            <div style={{ marginTop: "var(--space-large)" }}>
                <Content spacing="base">
                    <Heading level={3}>Sync History</Heading>
                    <div style={{ fontFamily: "monospace" }}>
                        <DataTable.Container>
                            <DataTable.Table>
                                <DataTable.Header style={{ fontFamily: "sans-serif" }}>
                                    <DataTable.HeaderCell>Date Range</DataTable.HeaderCell>
                                    <DataTable.HeaderCell>Status</DataTable.HeaderCell>
                                    <DataTable.HeaderCell><div style={{ textAlign: "right" }}>Jobs Synced</div></DataTable.HeaderCell>
                                    <DataTable.HeaderCell><div style={{ textAlign: "right" }}>Submitted</div></DataTable.HeaderCell>
                                </DataTable.Header>
                                <DataTable.Body>
                                    {isLoadingHistory ? (
                                        <DataTable.Row>
                                            <DataTable.Cell>Loading...</DataTable.Cell>
                                            <DataTable.Cell></DataTable.Cell>
                                            <DataTable.Cell></DataTable.Cell>
                                            <DataTable.Cell></DataTable.Cell>
                                        </DataTable.Row>
                                    ) : (
                                        syncHistory.map((job) => (
                                            <DataTable.Row key={job.id}>
                                                <DataTable.Cell>
                                                    <strong>{displayDate(job.start_date)}</strong> &ndash; <strong>{displayDate(job.end_date)}</strong>
                                                </DataTable.Cell>
                                                <DataTable.Cell>
                                                    <span style={{ fontFamily: "monospace" }}>
                                                        <StatusLabel
                                                            status={SYNC_JOB_STATUS_DISPLAY[job.status]}
                                                            label={SYNC_JOB_STATUS_LABEL[job.status]}
                                                        />
                                                    </span>
                                                </DataTable.Cell>
                                                <DataTable.Cell>
                                                    <div style={{ textAlign: "right" }}>
                                                        {job.jobs_synced}
                                                    </div>
                                                </DataTable.Cell>
                                                <DataTable.Cell>
                                                    <div style={{ textAlign: "right" }}>
                                                        {displayTimestamp(job.submitted_at)}
                                                    </div>
                                                </DataTable.Cell>
                                            </DataTable.Row>
                                        ))
                                    )}
                                </DataTable.Body>
                            </DataTable.Table>
                        </DataTable.Container>
                    </div>
                </Content>
            </div>
        </DashboardTab>
    );
}

export default SyncJobs;
